import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { useNotifications } from "../hooks/notificationsContext";
import useAuthUser from "../hooks/useAuthUser";
import { processUser } from "../utils/userUtils";
import ConfirmModal from "./ConfirmModal";
import "./tradingDashboard.css";
import { createPortal } from "react-dom";

export default function JackpotPanel({ userData }) {
  const [jackpotData, setJackpotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [user, setUser] = useState(null);

  const authUser = useAuthUser();
  const { addNotification } = useNotifications();
  
  const isLotteryLocked = userData.userRank < 5;

  useEffect(() => {
    const docRef = doc(db, "Global_Vars", "Jackpot");
    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          setJackpotData(snap.data());
        } else {
          setJackpotData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching jackpot data:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUser) return;
    setUser(processUser(authUser));
  }, [authUser]);

  const handleBuyTicket = () => {
    if (!jackpotData || !user) return;

    const ticketPrice = jackpotData.jackpotPrice ?? 0;
    const balance = user.money ?? 0;

    if (balance < ticketPrice) {
      setModalMessage("Not enough funds to buy a ticket!");
      setShowConfirm(true);
      return;
    }

    setModalMessage(
      `Buy a ticket for ${ticketPrice.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })}?`
    );
    setShowConfirm(true);
  };

const confirmPurchase = async () => {
  const auth = getAuth();
  const activeUid = auth.currentUser?.uid || user?.uid;
  const activePrice = jackpotData?.jackpotPrice;

  if (!activeUid) return;

  try {
    setModalMessage("PROCESSING...");
    const token = await auth.currentUser.getIdToken();

    const res = await fetch(
      "https://buyjackpotticket-5dhfwyjvzq-uc.a.run.app",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          uid: activeUid,
          ticketPrice: activePrice,
        }),
      }
    );

    if (res.ok) {
      setModalMessage("TICKET PURCHASED");
      addNotification("Jackpot ticket purchased.", "success");
    } else {
      const errorData = await res.json();
      throw new Error(errorData.message || "Server rejected request");
    }

  } catch (err) {
    console.error("Purchase Error:", err);
    setModalMessage("TRANSACTION FAILED");
    addNotification(err.message || "Purchase failed.", "error");
  } finally {
    setTimeout(() => {
      setShowConfirm(false);
      setModalMessage("");
    }, 1500);
  }
};

  if (loading) return <div className="panel">Loading Jackpot...</div>;
  if (!jackpotData) return <div className="panel">No jackpot data available.</div>;

return (
  <div className="jackpot-panel" style={{ 
    fontFamily: "'JetBrains Mono', monospace", 
    fontSize: "0.7rem", 
    color: "#eee",
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  }}>
    {/* High-Density Info Grid */}
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", rowGap: "4px" }}>
      <span style={{ color: "#888" }}>JACKPOT:</span>
      <span style={{ color: "#50e3c2", fontWeight: "bold" }}>
        {jackpotData.jackpot?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
      </span>

      <span style={{ color: "#888" }}>TICKET:</span>
      <span>
        {jackpotData.jackpotPrice?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
      </span>

      <span style={{ color: "#888" }}>SOLD:</span>
      <span>{jackpotData.jackpotTicketsSold}</span>

<span style={{ color: "#888" }}>OWNED:</span>
<span style={{ whiteSpace: "nowrap" }}>
  {user?.jackpotTicketsOwned ?? 0}
  {user?.jackpotTicketsOwned > 0 && jackpotData.jackpotTicketsSold > 0 && (() => {
    const percentage = (user.jackpotTicketsOwned / jackpotData.jackpotTicketsSold) * 100;
    
    return (
      <span style={{ color: "#50e3c2", fontSize: "0.6rem", marginLeft: "4px" }}>
        ({percentage < 0.1 ? "<0.1" : percentage.toFixed(1)}%)
      </span>
    );
  })()}
</span>

      <span style={{ color: "#888" }}>DRAWING:</span>
      <span style={{ fontSize: "0.65rem" }}>
        {(() => {
          const d = new Date(jackpotData.jackpotTimestamp);
          return `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
        })()}
      </span>

      <span style={{ color: "#888" }}>WINNER:</span>
      <span style={{ color: "#ff9800", fontSize: "0.65rem", overflow: "hidden", textOverflow: "ellipsis" }}>
        {jackpotData.jackpotWinner || "NONE"}
      </span>
    </div>

    {/* Integrated Action Button */}
<button 
  className={`jetbrains-btn ${isLotteryLocked ? "disabled" : ""}`} 
  onClick={handleBuyTicket}
  disabled={isLotteryLocked}
  style={{
    marginTop: "4px",
    width: "100%",
    padding: "6px 0",
    background: isLotteryLocked ? "rgba(255, 255, 255, 0.05)" : "rgba(80, 227, 194, 0.1)",
    color: isLotteryLocked ? "#8b949e" : "#50e3c2",
    border: isLotteryLocked ? "1px solid #30363d" : "1px solid #50e3c2",
    fontSize: "0.65rem",
    cursor: isLotteryLocked ? "not-allowed" : "pointer",
    fontFamily: "'JetBrains Mono', monospace",
    textTransform: "uppercase",
    opacity: isLotteryLocked ? 0.6 : 1
  }}
>
  {isLotteryLocked ? "LOCKED" : "Buy Ticket"}
</button>

{showConfirm && createPortal(
  <ConfirmModal
    message={modalMessage}
    onConfirm={confirmPurchase} 
    onCancel={() => {
      setShowConfirm(false);
      setModalMessage("");
    }}
    confirmText="Confirm"
    cancelText="Cancel"
    showConfirmButton={modalMessage.startsWith("Buy")} 
  />,
  document.body
)}
  </div>
);
}
